/* @flow */
import { Set, Map, List, OrderedMap } from 'immutable';
import { getterFromKeyPath, alphabetize } from 'trustar/utils';
import { createSelector, defaultMemoize } from 'reselect';

import { displayOptions } from 'modules/reports/getters';
import { fetchParams } from 'modules/indicators/getters';

export const attributes = getterFromKeyPath(['tags', 'attributes']);
export const enclaveTags = getterFromKeyPath(['tags', 'enclaveTags']);
export const indicatorTags = getterFromKeyPath(['tags', 'indicatorTags']);
export const loading = getterFromKeyPath(['tags', 'loading']);
export const modalOpen = getterFromKeyPath(['tags', 'modalOpen']);

// Search 2.0, we use the query payloads as the keeper of our tags
const reportsFilterTags = getterFromKeyPath(['search', 'reportPageQuery', 'tagFilters']);
const entityFilterTags = getterFromKeyPath(['search', 'entityPageQuery', 'tagFilters']);


/* This reduces the indicatorsTags getter value above of <enclaveId, tags[]>
 * to a list of all tags (tags[]), for use in the tags filter getters below
 */
export const indicatorTagsList = createSelector(
  indicatorTags,
  (tags: OrderedMap<string, any>) => tags
    .reduce((tagList, tagSet) => tagList.concat(tagSet.toList()), List())
);

/* NOTE: About tags for filtering reports list by tag: (︶︿︶)
 *
 * Right now there can be multiple enclave tags with the same name.
 * We de-duplicate the tags list here and treat the tags as if the name
 * is the uniquely identifying element. This is mainly because from the server
 * perspective it will return all tags of a given name, so although this is a
 * little strange now it is consistent with the current server behavior.
 *
 * NOTE: These getters provide the tag options for reports and indicators.
 * Because the logic is the same and the data objects are the same shape, the
 * operations here are shared for both.
 *
 **/

/* De-duplicate tags based on name, i.e. remove repreated names */
const deDeuplicateTagsByName = (tags: OrderedMap<string, any>) => {
  let nameSet = Set();
  return tags.reduce((tags, tag) => {
    if (!nameSet.has(tag.get('name'))) {
      nameSet = nameSet.add(tag.get('name'));
      return tags.set(tag.get('guid'), tag);
    }
    return tags;
  }, OrderedMap());
};

/*
 * Calculations for sets of currently included and excluded tag names
 */
const includedNameSet = (options: Map<string, any>) =>
  options
    .get('tags')
    .map(tag => tag.get('name'))
    .toSet();

export const includedTagNameSetReports = createSelector(displayOptions, includedNameSet);
export const includedTagNameSetIndicators = createSelector(fetchParams, includedNameSet);

const excludedNameSet = (options: Map<string, any>) =>
  options
    .get('excludedTags')
    .map(tag => tag.get('name'))
    .toSet();

export const excludedTagNameSetReports = createSelector(displayOptions, excludedNameSet);
export const excludedTagNameSetIndicators = createSelector(fetchParams, excludedNameSet);

/*
 * Calculations for included and excluded tag options
 */
const includedTagOptions = (tags: OrderedMap<string, any>, excludedTags) => {
  return deDeuplicateTagsByName(tags)
    .filter(tag => !excludedTags.has(tag.name))
    .sort(alphabetize);
};

export const includedTagOptionsReports = createSelector(
  [ enclaveTags, excludedTagNameSetReports ], includedTagOptions
);

export const includedTagOptionsIndicators = createSelector(
  [ indicatorTagsList, excludedTagNameSetIndicators ], includedTagOptions
);

const excludedTagOptions = (tags: OrderedMap<string, any>, activeTags) => {
  return deDeuplicateTagsByName(tags)
    .filter(tag => !activeTags.has(tag.name))
    .sort(alphabetize);
};

export const excludedTagOptionsReports = createSelector(
  [ enclaveTags, includedTagNameSetReports ],
  excludedTagOptions
);

export const excludedTagOptionsIndicators = createSelector(
  [ indicatorTagsList, includedTagNameSetIndicators ],
  excludedTagOptions
);

// Search 2.0
const mergeTagOptions = (includedTags: OrderedMap<string, any>, excludedTags: OrderedMap<string, any>, existingTags: Map<string, any>) => {
  if (existingTags) { // If you provide an existing query object, it will filter out the already selected tag options
    const existingIncludedTags = existingTags.get('included');
    const existingExcludedTags = existingTags.get('excluded');
    return {
      included: includedTags.toSet().subtract(existingExcludedTags).sort(alphabetize),
      excluded: excludedTags.toSet().subtract(existingIncludedTags).sort(alphabetize)
    };
  }

  return {
    included: includedTags.toSet().sort(alphabetize),
    excluded: excludedTags.toSet().sort(alphabetize)
  };
};

export const allTagOptionsReports = createSelector(
  [includedTagOptionsReports, excludedTagOptionsReports, reportsFilterTags ], mergeTagOptions
);

export const allTagOptionsEntities = createSelector(
  [includedTagOptionsIndicators, excludedTagOptionsIndicators, entityFilterTags ], mergeTagOptions
);

// Show all possible tag options on the search page, the filtering will be handled
export const allTagOptionsSearch = createSelector(
  [allTagOptionsReports, allTagOptionsEntities],
  (reportTags = {included: Set(), excluded: Set()}, entitiesTags = {included: Set(), excluded: Set()}) => {


    return {
      included: reportTags.included.union(entitiesTags.included).sort(alphabetize),
      excluded: reportTags.excluded.union(entitiesTags.excluded).sort(alphabetize),
    };
  }
);
// End Search 2.0


// WEBPACK FOOTER //
// ./src/app/modules/tags/getters.js